package fr.frux.feature.notifications

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import fr.frux.core.common.error.AppError
import fr.frux.core.common.error.asAppError
import fr.frux.core.common.logging.Logger
import fr.frux.core.common.push.PushRegistrar
import fr.frux.core.common.push.PushRegistrationResult
import fr.frux.shared.domain.model.NotificationPreferences
import fr.frux.shared.domain.repository.NotificationRepository
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

data class NotificationPreferencesState(
    val preferences: NotificationPreferences? = null,
    val isLoading: Boolean = false,
    val isSaving: Boolean = false,
    /** Refus à annoncer (#176), typé : le libellé est résolu par l'écran. */
    val error: NotificationPreferencesError? = null,
    /** `false` sur les plateformes sans push (web, desktop, iOS avant le M4) : le toggle s'y grise
     *  au lieu d'échouer à l'usage (#252, ADR 0003). */
    val pushSupported: Boolean = true,
    /** Vrai pendant un `ensureRegistered()` en vol côté toggle (#252, revue T15) : le Switch se
     *  désactive et un second tap ne relance pas une seconde demande de permission concurrente. */
    val isRegisteringPush: Boolean = false,
)

/**
 * Préférences de notification (#225 P0, ADR 0017 D9) : `inAppEnabled` est affiché non modifiable
 * (le serveur l'ignore silencieusement de toute façon, cf. `NotificationsApi.updatePreferences`).
 * Chaque toggle sauvegarde immédiatement — pas de bouton « Enregistrer » séparé, même patron que
 * les autres écrans de réglages ponctuels de l'app.
 */
class NotificationPreferencesViewModel(
    private val notificationRepository: NotificationRepository,
    private val pushRegistrar: PushRegistrar,
) : ViewModel() {
    private val _state = MutableStateFlow(NotificationPreferencesState(pushSupported = pushRegistrar.supported))
    val state: StateFlow<NotificationPreferencesState> = _state.asStateFlow()

    init {
        load()
    }

    fun load() {
        viewModelScope.launch {
            _state.update { it.copy(isLoading = true, error = null) }
            notificationRepository.getPreferences().fold(
                onSuccess = { prefs ->
                    _state.update { it.copy(preferences = prefs, isLoading = false) }
                    reconcilePushToken(prefs)
                },
                onFailure = { error ->
                    _state.update {
                        it.copy(isLoading = false, error = NotificationPreferencesError.Failure(error.asAppError()))
                    }
                },
            )
        }
    }

    /**
     * Réconciliation du jeton (#252, revue T15 CRITICAL 2) : `pushEnabled` côté serveur peut
     * survivre à une révocation locale (déconnexion puis reconnexion sur le même appareil, jeton
     * révoqué à la sortie) sans qu'aucun jeton ne soit réellement enregistré — le toggle mentirait
     * silencieusement. Silencieux à dessein : ce n'est pas un geste de l'utilisateur, une erreur
     * réseau ici ne doit pas afficher de message, seul un refus de permission avéré corrige l'état.
     */
    private suspend fun reconcilePushToken(prefs: NotificationPreferences) {
        if (!prefs.pushEnabled || !pushRegistrar.supported) return
        // Permission révoquée dans les réglages système (#285) : corriger la préférence sans
        // passer par ensureRegistered, qui redemanderait la permission — une boîte de dialogue
        // surgirait à la simple ouverture de l'écran.
        if (!pushRegistrar.permissionGranted()) {
            save { it.copy(pushEnabled = false) }
            return
        }
        if (pushRegistrar.ensureRegistered() == PushRegistrationResult.PermissionDenied) {
            save { it.copy(pushEnabled = false) }
        }
    }

    fun setEmailEnabled(enabled: Boolean) = save { it.copy(emailEnabled = enabled) }

    /**
     * Activer le push demande d'abord la permission système et l'enregistrement de l'appareil
     * (#252 P2) : sans jeton actif, le serveur retomberait sur l'email et la préférence mentirait.
     * Un refus fait retomber le toggle à `false` avec un message — jamais un état « activé » qui ne
     * délivre rien.
     *
     * Désactiver ne révoque PAS le jeton : la préférence serveur suffit à couper les envois, et
     * garder le jeton rend la réactivation instantanée, sans nouvelle boîte de dialogue système.
     */
    fun setPushEnabled(enabled: Boolean) {
        if (!enabled) {
            save { it.copy(pushEnabled = false) }
            return
        }
        // Garde de réentrance (#252, revue T15 IMPORTANT 3) : un double tap ne doit pas relancer
        // une seconde demande de permission concurrente pendant que la première est en vol.
        if (_state.value.isRegisteringPush) return
        _state.update { it.copy(isRegisteringPush = true) }
        viewModelScope.launch {
            try {
                when (val result = pushRegistrar.ensureRegistered()) {
                    PushRegistrationResult.Registered -> {
                        save { it.copy(pushEnabled = true) }
                    }

                    PushRegistrationResult.PermissionDenied -> {
                        // Message neutre (#252, revue T15 MINOR 6) : `grantResults` vide (dialogue
                        // annulé plutôt qu'explicitement refusé) rend le même résultat qu'un refus
                        // réel — on ne peut pas distinguer les deux, donc pas de fausse piste
                        // « va dans les réglages » qui ne s'appliquerait qu'au second cas.
                        _state.update { it.copy(error = NotificationPreferencesError.PushPermissionDenied) }
                    }

                    PushRegistrationResult.Unsupported -> {
                        _state.update { it.copy(error = NotificationPreferencesError.PushUnsupported) }
                    }

                    is PushRegistrationResult.Failed -> {
                        _state.update { it.copy(error = NotificationPreferencesError.PushRegistrationFailed) }
                        Logger.e(TAG, "ensureRegistered a échoué : ${result.message}")
                    }
                }
            } finally {
                _state.update { it.copy(isRegisteringPush = false) }
            }
        }
    }

    /** Acquitte le message d'erreur affiché en snackbar (#252, revue T15 CRITICAL 1) — même patron
     *  que `ProfileViewModel.onMessageShown` : one-shot porté par le state, pas par un `remember`
     *  d'écran (détruit à chaque recomposition de l'entryProvider Nav3). */
    fun onErrorShown() {
        _state.update { it.copy(error = null) }
    }

    fun setHarvestEvents(enabled: Boolean) = save { it.copy(harvestEvents = enabled) }

    fun setTreeEvents(enabled: Boolean) = save { it.copy(treeEvents = enabled) }

    fun setMaturityAlerts(enabled: Boolean) = save { it.copy(maturityAlerts = enabled) }

    /** Optimiste (le toggle change tout de suite), rollback sur échec — même patron que FavoritesStore.toggle. */
    private fun save(transform: (NotificationPreferences) -> NotificationPreferences) {
        val current = _state.value.preferences ?: return
        val target = transform(current)
        _state.update { it.copy(preferences = target, isSaving = true, error = null) }
        viewModelScope.launch {
            notificationRepository
                .updatePreferences(
                    emailEnabled = target.emailEnabled,
                    pushEnabled = target.pushEnabled,
                    harvestEvents = target.harvestEvents,
                    treeEvents = target.treeEvents,
                    maturityAlerts = target.maturityAlerts,
                ).fold(
                    onSuccess = { saved -> _state.update { it.copy(preferences = saved, isSaving = false) } },
                    onFailure = { error ->
                        _state.update {
                            it.copy(
                                preferences = current,
                                isSaving = false,
                                error = NotificationPreferencesError.Failure(error.asAppError()),
                            )
                        }
                    },
                )
        }
    }

    private companion object {
        const val TAG = "NotificationPreferencesVM"
    }
}

/**
 * Pourquoi l'écran de préférences a quelque chose à annoncer (#176). Les trois refus de push (#252)
 * ne viennent pas d'un appel API : ils n'ont pas de catégorie [AppError] derrière eux.
 */
sealed interface NotificationPreferencesError {
    /** Permission refusée, ou dialogue annulé — indistinguables côté Android (#252, revue T15). */
    data object PushPermissionDenied : NotificationPreferencesError

    data object PushUnsupported : NotificationPreferencesError

    data object PushRegistrationFailed : NotificationPreferencesError

    data class Failure(
        val error: AppError,
    ) : NotificationPreferencesError
}
